import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Separator } from '@/components/ui/separator';
import Title from '@/components/ui/Title';
import { Plus, Trash2 } from 'lucide-react';
import type { IndexItemInput, WebinarIndex } from '@/features/webinar/types';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Skeleton } from '@/components/ui/skeleton';

import ReaddIcon from '@/components/icons/ReaddIcon';
import { replaceIndexItems } from '@/features/webinar/webinarThunks';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import { toast } from 'react-toastify';
import type { AppError } from '@/features/auth/types';
import { mapHttpError } from '@/lib/mapApiError';
import IndexPreview from './IndexPreview';
import { toWebinarLng } from '@/lib/webinarLanguage';

const makeIndexItemSchema = (t: TFunction) =>
  z.object({
    title: z
      .string()
      .min(1, t('waitingRoom.index.errors.titleRequired'))
      .max(150, t('waitingRoom.index.errors.titleMax', { max: 150 }))
      .refine((val) => val.trim().length > 0, {
        message: t('waitingRoom.index.errors.titleRequired'),
      }),
    description: z
      .union([
        z.string().max(300, t('waitingRoom.index.errors.descriptionMax', { max: 300 })),
        z.literal(''),
      ])
      .optional(),
    showDescription: z.boolean(),
  });

type IndexFormValues = {
  items: Array<{
    title: string;
    description?: string | '';
    showDescription: boolean;
  }>;
};

export default function IndexForm({
  webinarId,
  showIndex,
  currentWebinarIndex,
  indexLoading,
  isEdit,
  isEvergreen,
}: {
  webinarId: number;
  showIndex?: boolean;
  currentWebinarIndex?: WebinarIndex;
  indexLoading?: boolean;
  isEdit?: boolean;
  isEvergreen?: boolean;
}) {
  const { t } = useTranslation('webinars');
  const { t: tCW } = useTranslation('createWebinar');
  const webinarLanguage = useAppSelector((state) => state.webinar.currentWebinar?.language);
  const { t: tW } = useTranslation('webinars', { lng: toWebinarLng(webinarLanguage) });

  const indexFormSchema = useMemo(
    () =>
      z.object({
        items: z
          .array(makeIndexItemSchema(t))
          .min(1, t('waitingRoom.index.errors.minTitleRequired'))
          .refine((items) => items.some((item) => item.title && item.title.trim().length > 0), {
            message: t('waitingRoom.index.errors.minTitleRequired'),
            path: ['items'],
          }),
      }),
    [t],
  );
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);

  const visualIdentity = useAppSelector(
    (state) => state.workspace.currentWorkspace?.visualIdentity,
  );
  const currentWaitingRoomPage = useAppSelector((state) => state.webinar.currentWaitingRoomPage);

  const theme = visualIdentity?.theme;

  const gradientOne = `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1 || '#000'}, ${visualIdentity?.gradient1Color2 || '#fff'})`;
  const gradientTwo = `linear-gradient(226deg, ${visualIdentity?.gradient2Color1 || '#000'} 0.2%, ${visualIdentity?.gradient2Color2 || '#fff'} 104.63%)`;
  const normalizedBackgroundImage =
    currentWaitingRoomPage?.backgroundImageUrl &&
    currentWaitingRoomPage.backgroundImageUrl.trim().length > 0
      ? decodeURIComponent(currentWaitingRoomPage.backgroundImageUrl)
      : undefined;

  const getPreviewBackground = () => {
    if (!currentWaitingRoomPage) {
      return visualIdentity?.backgroundColor || '#FBFBFB';
    }

    const { background, backgroundGradient } = currentWaitingRoomPage;

    if (background === 'GRADIENT') {
      return backgroundGradient === '2' ? gradientTwo : gradientOne;
    }

    return visualIdentity?.backgroundColor || '#FBFBFB';
  };

  const previewBackground = getPreviewBackground();
  const previewBackgroundStyle =
    currentWaitingRoomPage?.background === 'IMAGE' && normalizedBackgroundImage
      ? {
          backgroundImage: `url(${normalizedBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : {
          background: previewBackground,
        };

  const getInitialValues = useMemo(() => {
    if (currentWebinarIndex && currentWebinarIndex.webinarId === webinarId) {
      if (currentWebinarIndex.items.length > 0) {
        return {
          items: currentWebinarIndex.items.map((item) => ({
            title: item.title ?? '',
            description: item.description ?? '',
            showDescription: item.description ? item.description.trim().length > 0 : false,
          })),
        };
      }
    }
    return {
      items: [
        {
          title: tW('waitingRoom.index.defaultItemTitle', { number: 1 }),
          description: tW('waitingRoom.index.defaultItemDescription', { number: 1 }),
          showDescription: true,
        },
      ],
    };
  }, [currentWebinarIndex, webinarId, tW]);

  // Lu par le reset ci-dessous sans en être une dépendance : un changement de langue ne doit pas
  // écraser la saisie en cours.
  const fallbackItemRef = useRef({ title: '', description: '', showDescription: true });
  useEffect(() => {
    fallbackItemRef.current = {
      title: tW('waitingRoom.index.defaultItemTitle', { number: 1 }),
      description: tW('waitingRoom.index.defaultItemDescription', { number: 1 }),
      showDescription: true,
    };
  }, [tW]);

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    reset,
    watch,
    handleSubmit,
  } = useForm<IndexFormValues>({
    resolver: zodResolver(indexFormSchema),
    defaultValues: getInitialValues,
    mode: 'onChange',
  });

  const {
    fields,
    append,
    remove: removeFieldArrayItem,
  } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedItems = watch('items');

  const hasIndexItems = watchedItems.some((item) => item.title.trim().length > 0);

  const buildItemsPayload = (items: IndexFormValues['items']): IndexItemInput[] =>
    items
      .filter((item) => item.title.trim().length > 0)
      .map((item, index) => ({
        title: item.title.trim(),
        description: item.showDescription ? (item.description || '').trim() : '',
        position: index,
      }));

  const persistItems = async (items: IndexFormValues['items']): Promise<boolean> => {
    if (!workspaceId || !webinarId) {
      console.error('Workspace ID or Webinar ID is missing');
      return false;
    }

    const itemsToSave = buildItemsPayload(items);

    if (itemsToSave.length === 0) {
      return false;
    }

    try {
      await dispatch(
        replaceIndexItems({
          workspaceId,
          webinarId,
          input: { items: itemsToSave },
        }),
      ).unwrap();
      return true;
    } catch (error) {
      const appError = error as AppError | undefined;

      if (appError?.message) {
        const mappedMessage = mapHttpError(appError.message);
        const translatedMessage = t(mappedMessage, appError.message);
        toast.error(translatedMessage);
      } else {
        toast.error(t('errors.generic', 'Une erreur est survenue lors de la sauvegarde.'));
      }

      if (appError?.details && Array.isArray(appError.details)) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            const mappedMessage = mapHttpError(detail.message);
            const translatedMessage = t(mappedMessage, detail.message);
            toast.error(translatedMessage);
          }
        });
      }
      return false;
    }
  };

  const [accordionValue, setAccordionValue] = useState<string>(
    isEdit ? '' : isEvergreen ? '' : currentWaitingRoomPage?.displayIndex ? 'item-1' : '',
  );
  const [previewAccordionValues, setPreviewAccordionValues] = useState<string[]>([]);

  useEffect(() => {
    if (isEdit) {
      setAccordionValue('');
      return;
    }
    if (showIndex !== undefined) {
      if (!showIndex) {
        setAccordionValue('');
      } else {
        setAccordionValue(isEvergreen ? '' : 'item-1');
      }
    } else {
      setAccordionValue(!isEvergreen && currentWaitingRoomPage?.displayIndex ? 'item-1' : '');
    }
  }, [showIndex, currentWaitingRoomPage?.displayIndex, isEdit, isEvergreen]);

  const onSubmit = async (data: IndexFormValues) => {
    const success = await persistItems(data.items);
    toast.success(t('waitingRoom.index.savedSuccess'));
    if (success) {
      setAccordionValue('');
    }
  };

  const addItem = () => {
    const nextIndex = fields.length + 1;
    append({
      title: tW('waitingRoom.index.defaultItemTitle', { number: nextIndex }),
      description: tW('waitingRoom.index.defaultItemDescription', { number: nextIndex }),
      showDescription: true,
    });
  };

  const removeItem = (index: number) => {
    if (fields.length <= 1) {
      reset(
        {
          items: [
            {
              title: tW('waitingRoom.index.defaultItemTitle', { number: 1 }),
              description: tW('waitingRoom.index.defaultItemDescription', { number: 1 }),
              showDescription: true,
            },
          ],
        },
        { keepDirty: true },
      );
      return;
    }

    removeFieldArrayItem(index);
  };

  const toggleDescription = (index: number) => {
    const currentItems = watch('items');
    const newItems = [...currentItems];
    const field = fields[index];
    const isEnabling = !newItems[index].showDescription;

    newItems[index] = {
      ...newItems[index],
      showDescription: isEnabling,
    };
    reset({ items: newItems }, { keepDirty: true });

    if (isEnabling && field) {
      const itemValue = `item-${field.id}`;
      setPreviewAccordionValues((prev) => {
        if (!prev.includes(itemValue)) {
          return [...prev, itemValue];
        }
        return prev;
      });
    }
  };

  useEffect(() => {
    if (!currentWebinarIndex || !webinarId) {
      return;
    }

    if (currentWebinarIndex.webinarId !== webinarId) {
      return;
    }

    const mappedItems =
      currentWebinarIndex.items.length > 0
        ? currentWebinarIndex.items.map((item) => ({
            title: item.title ?? '',
            description: item.description ?? '',
            showDescription: item.description.trim().length > 0,
          }))
        : [fallbackItemRef.current];

    reset({ items: mappedItems });
  }, [currentWebinarIndex, webinarId, reset]);

  useEffect(() => {
    const openItems = watchedItems
      .map((item, idx) => {
        const field = fields[idx];
        return item.showDescription && field ? `item-${field.id}` : null;
      })
      .filter((val): val is string => val !== null);
    setPreviewAccordionValues(openItems);
  }, [watchedItems, fields]);

  const shouldShowSkeleton =
    indexLoading && (!currentWebinarIndex || currentWebinarIndex.webinarId !== webinarId);

  if (shouldShowSkeleton) {
    return (
      <div className="bg-medium rounded-[20px] p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-6 w-20" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-6 rounded-lg" />
            <Skeleton className="h-6 w-6 rounded-lg" />
          </div>
        </div>
        <div className="space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-40 w-full rounded-[20px]" />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Accordion
        type="single"
        value={accordionValue}
        onValueChange={(value) => {
          setAccordionValue(value);
        }}
        collapsible
        className="space-y-[10px] bg-card rounded-[20px]"
      >
        <AccordionItem value="item-1" className=" p-6 ">
          <AccordionTrigger className="rounded-[20px] p-0" fillForm={hasIndexItems}>
            <div className="flex flex-col  gap-2">
              <Title level={2}>{t('waitingRoom.indexTitle')}</Title>
              <p className="text-sm text-gray-500">{t('waitingRoom.indexDescription')}</p>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4 rounded-[20px] pt-4 px-0 pb-0">
            <div className="flex flex-col gap-4">
              {fields.map((field, index) => (
                <div key={field.id} className="flex flex-col gap-4">
                  <div>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <Controller
                          name={`items.${index}.title`}
                          control={control}
                          render={({ field: titleField, fieldState }) => (
                            <TextFieldWithLimit
                              label={t('waitingRoom.index.partTitle', { number: index + 1 })}
                              value={titleField.value ?? ''}
                              className="bg-white"
                              onChange={(v) => titleField.onChange(v)}
                              maxLength={150}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </div>

                      <Controller
                        name={`items.${index}.showDescription`}
                        control={control}
                        render={({ field: descToggleField }) => (
                          <button
                            type="button"
                            className={`py-3 h-14 px-2 rounded-lg border border-input ${
                              descToggleField.value ? 'bg-[#95B6FF]' : 'bg-white'
                            }`}
                            onClick={() => toggleDescription(index)}
                          >
                            <ReaddIcon />
                          </button>
                        )}
                      />
                      {fields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="bg-white h-14 rounded-lg py-3 px-2 border border-input cursor-pointer"
                        >
                          <Trash2 className="size-6" />
                        </button>
                      )}
                    </div>
                  </div>
                  {watch(`items.${index}.showDescription`) && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Controller
                          name={`items.${index}.description`}
                          control={control}
                          render={({ field: descField, fieldState }) => (
                            <TextFieldWithLimit
                              label={t('waitingRoom.index.partDescription', { number: index + 1 })}
                              value={descField.value || ''}
                              onChange={(v) => descField.onChange(v)}
                              className="bg-white"
                              maxLength={300}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {errors.items && typeof errors.items.message === 'string' && (
              <p className="text-xs text-error">{errors.items.message}</p>
            )}
            <button type="button" onClick={addItem} className="flex items-center gap-2">
              <Plus />
              {t('waitingRoom.index.addPart')}
            </button>
            <Button
              type="submit"
              disabled={isSubmitting || !isValid}
              className="disabled:bg-gray-200 "
            >
              {isSubmitting && <Loader />} {tCW('save')}
            </Button>

            <Separator className="bg-input" />

            <IndexPreview
              visualIdentity={visualIdentity || undefined}
              theme={theme || undefined}
              previewBackgroundStyle={previewBackgroundStyle}
              watchedItems={watchedItems}
              fields={fields}
              previewAccordionValues={previewAccordionValues}
              setPreviewAccordionValues={setPreviewAccordionValues}
              defaultPreviewClosed={Boolean(isEvergreen)}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </form>
  );
}
